import { AUTHORS } from './authors';
import { PageMeta } from 'constants/pages';
import { BOOK_A_DEMO_LINK } from 'constants/common';
import { EHashtag, TCardProps } from 'types/common';
import { ArticleSections } from 'types/articles';

// TODO: Replace placeholder images with actual .webp images in assets/resources/ethics-rules-of-engagement-defense-drones/
import HeaderImage from 'assets/resources/ethics-rules-of-engagement-defense-drones/header-section.webp';
import EthicsImage from 'assets/resources/ethics-rules-of-engagement-defense-drones/ethics-engineering.webp';

const commonData = {
  ...AUTHORS.AILiveSim,
  title:
    'Training for Judgment: Why Ethics and Rules of Engagement Must Be Built into Defense Drone Development',
  publishDate: 'April 2, 2026',
  minutesOfRead: 8,
};

export const tenthApril2026ArticleMeta: PageMeta = {
  url: '/articles/ethics-rules-of-engagement-defense-drones',
  title: commonData.title,
  description:
    'Learn why ethics and rules of engagement must be engineered into defense drone software and how synthetic data enables scalable validation of operational constraints.',
};

export const tenthApril2026ArticleData: ArticleSections[] = [
  {
    resourceType: 'Article',
    image: HeaderImage,
    imageCaption: 'Training for Judgment in Defense Drone Development',
    type: 'header',
    linkedInPostId: 'https://www.linkedin.com/company/ailivesim/',
    text: (
      <>
        <p>
          <em>AILiveSim Defense Drone Series | Article 2 of 3</em>
        </p>
      </>
    ),
    ...commonData,
  },

    {
    type: 'common',
    title: 'Performance Alone Does Not Equal Readiness',
    text: (
      <>
        <p>
          A drone that detects accurately but behaves incorrectly under operational constraints is not
          mission ready. Defense systems operate under ethical frameworks, national doctrine, and rules
          of engagement that vary by force, jurisdiction, and mission type. What defense teams face is
          a question of permitted behavior as much as raw capability, and that question has to be
          answered through testing, before deployment.
        </p>
      </>
    ),
  },

  {
    type: 'common',
    title: 'Ethics Is an Engineering Problem, Not a Policy Checkbox',
    text: (
      <>
        <p>
          In autonomous and semi-autonomous systems, ethical constraints become system design problems.
          Engagement logic, escalation thresholds, target discrimination, maneuver permissions, and
          response boundaries all have to be represented in the software. That means they also should
          be testable.
        </p>
        <p>
          Two identical drone platforms, same hardware, same sensors, deployed by two different
          nations&apos; air forces, may need to behave meaningfully differently. What constitutes a
          permissible target or a proportionate response is not standardized across deployments. The
          software must encode those differences, and validation must confirm they hold under pressure.
        </p>
      </>
    ),
  },

  {
    type: 'common',
    title: 'Why Field Data Alone Cannot Cover the Ethical Decision Space',
    text: (
      <>
        <p>
          Ethically critical situations tend to be rare, dangerous to reproduce, or ambiguous enough
          that operators need to study them before deployment rather than after a failure. Real-world
          data is essential, but by itself it is too sparse and inconsistent to cover the full range of
          decision-making that a deployed system will encounter.
        </p>
        <p>
          A model trained without rules-of-engagement-specific scenarios will optimize for raw
          performance metrics that may conflict with operational mandates. A system that maximizes
          threat neutralization without engagement boundaries encoded as hard constraints will behave
          very differently from one that was trained with them. The gap between what the model learned
          and what it must do under constraints is where failures carry the highest consequences.
        </p>
      </>
    ),
    image: EthicsImage,
    imageCaption: 'Ethics as an engineering discipline',
  },

  {
    type: 'common',
    title: 'Why Synthetic Data Is the Practical Answer',
    text: (
      <>
        <p>
          Synthetic data makes it possible to generate controlled variations of ethically sensitive
          scenarios at scale. Teams can model uncertain identification, degraded visibility, civilian
          presence near targets, or conflicting sensor inputs. They can test how software responds when
          conditions are incomplete or contradictory.
        </p>
        <p>
          AILiveSim allows defense customers to configure simulation environments around
          country-specific doctrine and engagement logic. Different forces represent different
          permissible behaviors, and those frameworks are tested repeatedly across thousands of
          scenario variations rather than assumed on paper. The IST module operates as an automated red
          team, specifically searching for failure modes in the rules-of-engagement logic, probing
          where proportionality thresholds and confidence levels intersect with environmental
          complexity.
        </p>
      </>
    ),
  },

  {
    type: 'common',
    title: 'Why Auditable Validation Matters for Defense Programs',
    text: (
      <>
        <p>
          Procurement decisions, deployment approvals, and public legitimacy increasingly depend on
          proving that systems behave within defined constraints. A defense contractor selling to
          multiple allied nations needs to validate that the same platform operates correctly under
          each buyer&apos;s distinct operational framework.
        </p>
        <p>
          Simulation creates a documented test environment where those behaviors can be examined at
          scale. Test coverage can be mapped to specific rules-of-engagement requirements. Failures
          can be traced back to the exact scenarios and conditions that caused them. If a system
          behaves incorrectly in a specific urban engagement scenario under degraded visibility, that
          failure is logged, reproducible, and addressable. That audit trail matters for certification
          and for procurement authorities who need evidence, not verbal assurances.
        </p>
      </>
    ),
  },

  {
    type: 'common',
    title: 'The Program Advantage',
    text: (
      <>
        <p>
          Ethical behavior in defense autonomy must be trained and tested, not declared in a compliance
          document and hoped for in the field. Synthetic data gives teams a scalable way to explore the
          gray zones that matter most. AILiveSim turns doctrine and rules of engagement into something
          operationally testable, giving defense programs the documented confidence required to move
          from development into operational deployment.
        </p>
      </>
    ),
  },

  {
    type: 'common',
    title: 'Ready to Take the Next Step?',
    text: (
      <>
        <p>
          <strong>
            <a href={BOOK_A_DEMO_LINK} target="_blank" rel="noopener noreferrer">
              Book Your Demo Today
            </a>
          </strong>
        </p>
        <p>
          <strong>Resources:</strong>
        </p>
        <ul>
          <li>
            <a
              href="https://www.ailivesim.com/articles/thermal-camera-sensors-ailivesim-platform"
              target="_blank"
              rel="noopener noreferrer"
            >
              Thermal Camera Sensors &amp; AILiveSim
            </a>
          </li>
          <li>
            Read our White Paper about the sim to real gap:{' '}
            <a
              href="https://ailivesim.com/whitepapers/using-synthetic-data-from-ailivesim"
              target="_blank"
              rel="noopener noreferrer"
            >
              Using Synthetic Data from AILiveSim to Train Robust Maritime Detection Models
            </a>
          </li>
          <li>
            <a href="/articles/ailivesim-glossary">AILiveSim Glossary of Terms</a>
          </li>
        </ul>
      </>
    ),
  },
];

export const tenthApril2026ArticleCard: TCardProps = {
  description:
    'Learn why ethics and rules of engagement must be engineered into defense drone software and how synthetic data enables scalable validation of operational constraints.',
  image: HeaderImage,
  type: 'Article',
  category: 'articles',
  postUrl: tenthApril2026ArticleMeta.url,
  ...commonData,
  hashTags: [
    EHashtag.SyntheticData,
    EHashtag.DigitalTwin,
    EHashtag.DefenseTechnology,
    EHashtag.Robotics,
    EHashtag.ObjectDetection,
  ],
};
